import { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TranslationContext } from 'contexts/translation-context';
import styled from 'styled-components';
import { Radio } from '@mui/material';
import { Button } from 'components/common/buttons';
import { openModal } from 'components/common/modal/modal-actions';
import AmericanExpressIcon from 'src/assets/images/type-of-cards-payment/anericanExpress.svg';
import MasterCardIcon from 'src/assets/images/type-of-cards-payment/masterCard.svg';
import VisaIcon from 'src/assets/images/type-of-cards-payment/visa.svg';
import { Image } from 'components/common/image';
import { FormikProps } from 'formik';
import { JoinFormType, PackagePrices } from 'types/join';
import { Row } from 'components/common/grid';
import { useExecuteRecaptcha } from 'hooks/useExecuteRecaptcha';
import { PaymentErrorModal } from './payment-error-modal';
import { ContactMeRequest, fetchPaymentStatus } from '../actions';
import { PlanState } from '../reducers/plan';

const RowDetails = styled.div<{ hasBorder: boolean; isContactForPaymentCollapseOpen: boolean }>`
  width: 628px;
  @media (max-width: 999px) {
    width: 100%;
    padding: 10px 4px;
    min-width: 0;
  }
  height: ${({ isContactForPaymentCollapseOpen }) =>
    isContactForPaymentCollapseOpen ? 'auto' : '126px'};
  //   border: ${props => (props.hasBorder ? '1px solid #d0d0d0' : 'none')};
  //   background: ${props => (props.hasBorder ? 'white' : 'f0f0f0')};
  display: flex;
  flex-direction: column; // Change to column direction
  align-items: flex-start;
  padding: 20px;
  box-sizing: border-box;
`;
const PaymentOption = styled.div<{ checked: boolean; isContactForPaymentCollapseOpen: boolean }>`
  width: 596px;
  height: ${({ isContactForPaymentCollapseOpen }) =>
    isContactForPaymentCollapseOpen ? '126px' : '61px'};
  border: 1px solid #d0d0d0;
  background: ${({ checked }) => (checked ? '#ffffff' : '#f0f0f0')};
  padding: 20px;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  align-items: center;
  box-sizing: border-box;

  @media (max-width: 999px) {
    width: 100%;
    min-width: 0;
    padding: 10px 4px;
    height: auto;
  }
`;
const LabelButton = styled.button`
  background: none;
  border: none;
  font-size: 13px;
  font-family: ${props => props.theme.fonts.montserratSemiBold};
  color: ${props => props.theme.colors.siteBlack};
  margin-left: 8px;
  margin-right: 8px;
  cursor: pointer;
  @media (max-width: 999px) {
    margin-left: 0;
    margin-right: 0;
    font-size: 12px;
  }
`;
const ContactMeButton = styled(Button)`
  width: 138px;
  height: 40px;
  border-radius: 3px;
  @media (max-width: 999px) {
    width: 100%;
    min-width: 0;
    height: 36px;
    font-size: 13px;
  }
`;
const StyleImg = styled.div`
  display: flex;
  gap: 10px;
  margin-left: 320px;
  @media (max-width: 999px) {
    margin-left: 8px;
    gap: 6px;
  }
`;

const WhiteBox = styled.div`
  margin-top: 50px;
  margin-left: 20px;
  width: 595px;
  padding: 20px;
  line-height: 1.9;
  background-color: ${props => props.theme.colors.white};
  box-sizing: border-box;
  @media (max-width: 999px) {
    width: 100%;
    margin-left: 0;
    margin-top: 24px;
    padding: 12px 6px;
  }
`;

const MainTitle = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.siteBlack};
  font-family: ${props => props.theme.fonts.montserratSemiBold};
`;
const Title = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.siteBlack};
  font-family: ${props => props.theme.fonts.montserratRegular};
`;
const Text = styled.div`
  font-size: 12px;
  margin-left: 2px;
  color: #9b9b9b;
  font-family: ${props => props.theme.fonts.montserratRegular};
`;

type PaymentDetailsProps = {
  isConfirmed: boolean;
  setIsConfirmed: (data: boolean) => void;
  id: string;
  setShowSuccessPage: (data: boolean) => void;
  setShowSuccessPageForPriceList: (data: boolean) => void;
  setContactMEThankYouScreenForPriceList: (data: boolean) => void;
  subscriptionType: string | undefined;
  isYourPlanActive: boolean;
  form: FormikProps<JoinFormType>;
  selectedPlan?: PackagePrices;
  removeCacheForm: () => void;
};
export const PaymentDetailsSection = ({
  setIsConfirmed,
  isConfirmed,
  id,
  setShowSuccessPage,
  isYourPlanActive,
  setShowSuccessPageForPriceList,
  setContactMEThankYouScreenForPriceList,
  subscriptionType,
  form,
  selectedPlan,
  removeCacheForm
}: PaymentDetailsProps) => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const dispatch = useDispatch();
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isContactForPaymentCollapseOpen, setIsContactForPaymentCollapseOpen] = useState(false);
  const { opportunityId, paymentLink, paymentStatus } =
    useSelector((state: { plan: PlanState }) => state.plan) || {};
  const [intervalID, setIntervalID] = useState(-1);
  const counterRef = useRef(0);
  const runRef = useRef(0);
  const [openedWindow, setOpenedWindow] = useState<Window | null>(null);
  const { execute } = useExecuteRecaptcha();

  useEffect(() => {
    if (isYourPlanActive) {
      setSelectedOption(null);
    }
  }, [isYourPlanActive]);

  const runTenTimes = useCallback(() => {
    counterRef.current++;
    if (runRef.current === 0 && form.values.email && form.values.email !== '' && opportunityId) {
      execute({
        callback: fetchPaymentStatus,
        params: [opportunityId, form.values.email]
      });
    }
  }, [opportunityId]);

  const handleCreditCardSelected = () => {
    if (paymentLink) {
      const newWindow = window.open(paymentLink, 'newWindow', 'height=600,width=800');
      if (newWindow) {
        setOpenedWindow(newWindow as Window);
        if (intervalID === -1) {
          setIntervalID(window.setInterval(runTenTimes, 15000));
        }
      }
    }
  };

  useEffect(() => {
    if (counterRef.current === 10) {
      clearInterval(intervalID);
    }
  }, [intervalID, runTenTimes]);

  const openErrorPaymentModal = () => {
    dispatch(
      openModal({
        component: <PaymentErrorModal />,
        modalClassName: 'registrationPaymentFaildModal'
      })
    );
  };

  useEffect(() => {
    if (opportunityId && form.values.email && form.values.email !== '') {
      execute({
        callback: fetchPaymentStatus,
        params: [opportunityId, form.values.email]
      });
    }
  }, [opportunityId]);

  useEffect(() => {
    if (paymentStatus === 'Failed') {
      runRef.current++;
      openErrorPaymentModal();
    } else if (paymentStatus === 'Paid' && openedWindow) {
      runRef.current++;
      setShowSuccessPageForPriceList(true);
      openedWindow.close();
      removeCacheForm();
    } else if (paymentStatus === 'Authorize' && openedWindow) {
      runRef.current++;
      setShowSuccessPage(true);
      openedWindow.close();
      removeCacheForm();
    }
  }, [
    paymentStatus,
    openedWindow,
    setShowSuccessPage,
    subscriptionType,
    setShowSuccessPageForPriceList
  ]);

  const handleRadioClick = (option: string) => {
    setIsContactForPaymentCollapseOpen(false);

    if (option === 'creditCard') {
      handleCreditCardSelected();
    } else if (option === 'bankTransfer') {
      // console.log('bankTransfer');
    } else if (option === 'contactForPayment') {
      setIsContactForPaymentCollapseOpen(true);
    }
    setSelectedOption(option);

    if (typeof setIsConfirmed === 'function') {
      setIsConfirmed(true);
    }
  };

  const contactMe = () => {
    if (opportunityId && form.values.email && form.values.email !== '') {
      execute({
        callback: ContactMeRequest,
        params: [opportunityId, form.values.email]
      });
      if (subscriptionType === 'Rapnet') {
        setShowSuccessPage(true);
      } else if (subscriptionType === 'PriceList') {
        setContactMEThankYouScreenForPriceList(true);
      }
      removeCacheForm();
    }
  };

  return (
    <div id={id}>
      {isConfirmed ? (
        <>
          <RowDetails
            hasBorder={!isContactForPaymentCollapseOpen}
            isContactForPaymentCollapseOpen={isContactForPaymentCollapseOpen}
          >
            <PaymentOption
              checked={selectedOption === 'creditCard'}
              isContactForPaymentCollapseOpen={false}
            >
              <Radio
                color="error"
                id="creditCardOption"
                name="creditCard"
                value="creditCard"
                checked={selectedOption === 'creditCard'}
                onChange={() => {
                  handleRadioClick('creditCard');
                }}
              />
              <LabelButton>{TRANSLATION_TEXT.credit_card}</LabelButton>
              <StyleImg>
                <Image src={AmericanExpressIcon} alt="American Express" />
                <Image src={MasterCardIcon} alt="MasterCard" />
                <Image src={VisaIcon} alt="Visa" />
              </StyleImg>
            </PaymentOption>
            {/*  <PaymentOption
              checked={selectedOption === 'contactForPayment'}
              isContactForPaymentCollapseOpen={isContactForPaymentCollapseOpen}
            >
              <Radio
                color="error"
                id="contactForPayment"
                name="contactForPayment"
                value="contactForPayment"
                checked={selectedOption === 'contactForPayment'}
                onChange={() => {
                  handleRadioClick('contactForPayment');
                }}
              />
              <LabelButton>{TRANSLATION_TEXT.contact_for_payment}</LabelButton>
              {isContactForPaymentCollapseOpen && (
                <RowDetails
                  hasBorder={!isContactForPaymentCollapseOpen}
                  isContactForPaymentCollapseOpen={isContactForPaymentCollapseOpen}
                >
                  <ContactMeButton blue onClick={() => contactMe()}>
                    {TRANSLATION_TEXT.contact_me}
                  </ContactMeButton>
                </RowDetails>
              )}
            </PaymentOption> */}
          </RowDetails>
          <WhiteBox>
            <MainTitle>{TRANSLATION_TEXT.membership_payment_terms}</MainTitle>
            <Row>
              <Title>{TRANSLATION_TEXT.recurring_payments}</Title>
              <Text>{TRANSLATION_TEXT.membership_fees_are_billed_automatically}</Text>
            </Row>
            <Row>
              <Title>{TRANSLATION_TEXT.billing_cycle}</Title>
              <Text>
                {
                  TRANSLATION_TEXT.charges_are_processed_within_14_days_before_your_subscription_renewal_date
                }
              </Text>
            </Row>
            {/* <Row>
              <Title>{TRANSLATION_TEXT.easy_cancellation}</Title>
              <Text>{TRANSLATION_TEXT.You_can_cancel_at_any_time_with_a_single_click}</Text>
              <Text>{TRANSLATION_TEXT.account_and_removing_your_saved_payment_method}</Text>
            </Row>
            <Row>
              <Title>{TRANSLATION_TEXT.account_approval_requirement}</Title>
              <Text>{TRANSLATION_TEXT.you_will_not_be_billed_until_your_rapnet_account_has}</Text>
              <Text>{TRANSLATION_TEXT.been_approved}</Text>
            </Row> */}
          </WhiteBox>
        </>
      ) : null}
    </div>
  );
};

export default PaymentDetailsSection;
